// src/automation/components/TemplateResetAudit.js
//
// Audit tab for KYC Template Reset: every run (scheduled / manual / test), who
// started it, dry or live, per-tenant totals, and — for the selected run — what
// happened to EVERY agent (template, status, message), filterable + exportable.
// Data: run_reports (index) + run_report (one run's saved report) — read-only.

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Download, AlertTriangle } from "lucide-react";
import { getRunReports, getRunReport } from "../api/automation_data";

const STATUS_LABEL = {
  applied: "Applied",
  matches: "Matches",
  would_change: "Would change",
  skipped_matching: "Already matching",
  agent_not_found: "Agent not in RCC",
  template_not_found: "Template missing",
  deferred: "Deferred (time budget)",
  aborted_auth: "Stopped (RCC auth)",
  error: "Error",
};
const OK = new Set(["applied", "matches", "skipped_matching"]);
const label = (s) => STATUS_LABEL[s] || (String(s).startsWith("http_") ? `HTTP ${s.slice(5)}` : s);

function fmtStamp(stamp) {
  // 20261103T170000Z -> local time
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(stamp || "");
  if (!m) return stamp || "—";
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])).toLocaleString();
}

function countsText(counts = {}, dry) {
  const main = dry
    ? `${counts.matches || 0} match · ${counts.would_change || 0} would change`
    : `${counts.applied || 0} applied` + (counts.skipped_matching ? ` · ${counts.skipped_matching} already matching` : "");
  const rest = Object.entries(counts)
    .filter(([k]) => !["applied", "matches", "would_change", "skipped_matching"].includes(k))
    .map(([k, v]) => `${v} ${label(k).toLowerCase()}`);
  return [main, ...rest].join(" · ");
}

function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function RunDetail({ taskName, stamp }) {
  const { data: rep, isLoading, error } = useQuery({
    queryKey: ["runReport", taskName, stamp],
    queryFn: () => getRunReport(taskName, stamp),
    enabled: !!stamp,
    staleTime: 10 * 60 * 1000,
  });
  const [tenant, setTenant] = useState("");
  const [status, setStatus] = useState("problems");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const out = [];
    for (const [tid, tr] of Object.entries(rep?.tenants || {})) {
      for (const [login, r] of Object.entries(tr.results || {})) {
        out.push({ tenant: tid, region: tr.region, login, template: r.template, status: r.status,
          message: r.message || (r.diffs ? `differs: ${r.diffs.join(", ")}` : ""),
          current: r.current_template || "", ms: r.ms });
      }
    }
    return out;
  }, [rep]);

  if (isLoading) return <div className="auto-empty">Loading run…</div>;
  if (error) return <div className="auto-eb-alert auto-eb-alert--err"><AlertTriangle size={13} /> {error.message}</div>;
  if (!rep) return null;
  if (rep.stopped) {
    return (
      <div className="auto-eb-alert auto-eb-alert--err">
        <AlertTriangle size={13} /> Run stopped before any RCC change: {rep.stopped}
      </div>
    );
  }

  const statuses = [...new Set(rows.map((r) => r.status))].sort();
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) =>
    (!tenant || r.tenant === tenant) &&
    (status === "" || (status === "problems" ? !OK.has(r.status) : r.status === status)) &&
    (!needle || r.login.toLowerCase().includes(needle) || String(r.template || "").toLowerCase().includes(needle)));
  const src = rep.source || {};

  return (
    <div className="auto-collapse auto-collapse--open">
      <div className="auto-collapse-head" style={{ cursor: "default" }}>
        <span className="auto-collapse-title">Run {fmtStamp(rep.stamp)}</span>
        <span className="auto-collapse-sub">
          {rep.dry_run ? "dry run" : "LIVE"} · {rep.seconds}s
          {src.file ? ` · ${src.file}${src.uploaded_utc ? ` (uploaded ${new Date(src.uploaded_utc).toLocaleString()}${src.uploaded_by ? ` by ${src.uploaded_by}` : ""})` : ""}` : ""}
          {src.mode === "assignments_override" ? " · assignments override" : ""}
        </span>
      </div>
      <div className="auto-collapse-body">
        <table className="auto-table">
          <thead>
            <tr><th>Tenant</th><th>Agents</th><th>Templates</th><th>Result</th><th>Seconds</th></tr>
          </thead>
          <tbody>
            {Object.entries(rep.tenants || {}).map(([tid, tr]) => (
              <tr key={tid}>
                <td className="auto-table-mono">{tid}</td>
                <td>{tr.agents ?? "—"}</td>
                <td>{tr.templates ?? "—"}{tr.templates_missing?.length ? ` (${tr.templates_missing.length} missing)` : ""}</td>
                <td>{tr.error ? <span className="auto-sched2-error">{tr.error}</span> : countsText(tr.counts, rep.dry_run)}</td>
                <td>{tr.stats?.seconds ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {Object.values(rep.conflicts || {}).some((c) => Object.keys(c).length) && (
          <p className="auto-eb-field-hint">
            Skipped (different templates in one tenant):{" "}
            {Object.entries(rep.conflicts).flatMap(([t, c]) => Object.keys(c).map((l) => `${l} (${t})`)).join(", ")}
          </p>
        )}

        <div className="auto-audit-bar" style={{ marginTop: 12 }}>
          <label className="auto-eb-field" style={{ maxWidth: 160 }}>
            <span className="auto-eb-field-label">Tenant</span>
            <select className="auto-eb-input" value={tenant} onChange={(e) => setTenant(e.target.value)}>
              <option value="">All tenants</option>
              {Object.keys(rep.tenants || {}).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="auto-eb-field" style={{ maxWidth: 220 }}>
            <span className="auto-eb-field-label">Status</span>
            <select className="auto-eb-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="problems">Needs attention / changes</option>
              <option value="">All agents</option>
              {statuses.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </label>
          <label className="auto-eb-field" style={{ maxWidth: 220 }}>
            <span className="auto-eb-field-label">Search</span>
            <input className="auto-eb-input" value={q} placeholder="login or template" onChange={(e) => setQ(e.target.value)} />
          </label>
          <button
            type="button"
            className="auto-eb-btn"
            onClick={() => downloadCsv(`template_reset_${rep.stamp}.csv`,
              ["tenant", "login", "template", "status", "current_template", "message", "ms"],
              rows.map((r) => [r.tenant, r.login, r.template, r.status, r.current, r.message, r.ms ?? ""]))}
          >
            <Download size={13} /> Export all agents (CSV)
          </button>
        </div>

        <div className="auto-table-wrap" style={{ maxHeight: 460, overflowY: "auto" }}>
          <table className="auto-table">
            <thead>
              <tr><th>Tenant</th><th>Agent</th><th>Template</th><th>Status</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {shown.slice(0, 1000).map((r) => (
                <tr key={`${r.tenant}/${r.login}`}>
                  <td className="auto-table-mono">{r.tenant}</td>
                  <td className="auto-table-mono">{r.login}</td>
                  <td className="auto-table-mono">{r.template}</td>
                  <td>{OK.has(r.status) ? "✅ " : r.status === "would_change" ? "🔄 " : "⚠️ "}{label(r.status)}</td>
                  <td>{r.current && r.status === "would_change" ? `now ${r.current}` : ""}{r.current && r.message ? " · " : ""}{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="auto-eb-field-hint">
          Showing {Math.min(shown.length, 1000)} of {shown.length} matching ({rows.length} agents in this run).
        </p>
      </div>
    </div>
  );
}

export default function TemplateResetAudit({ taskName }) {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["runReports", taskName],
    queryFn: () => getRunReports(taskName),
    staleTime: 30 * 1000,
  });
  const [stamp, setStamp] = useState(null);
  const runs = data?.runs || [];
  const active = stamp || runs[0]?.stamp || null;

  if (isLoading) return <div className="auto-empty">Loading audit…</div>;
  if (error) return <div className="auto-eb-alert auto-eb-alert--err"><AlertTriangle size={13} /> {error.message}</div>;
  if (!runs.length) {
    return <div className="auto-empty">No runs yet. Every run (scheduled, Run now, Test) is recorded here with what it did to each agent.</div>;
  }

  return (
    <div>
      <div className="auto-audit-bar">
        <span className="auto-audit-summary">{runs.length} run{runs.length === 1 ? "" : "s"} recorded</span>
        <button type="button" className="auto-eb-btn" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={13} className={isFetching ? "auto-eb-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="auto-table-wrap" style={{ maxHeight: 280, overflowY: "auto", marginBottom: 14 }}>
        <table className="auto-table">
          <thead>
            <tr><th>When</th><th>Type</th><th>By</th><th>Mode</th><th>Agents</th><th>Result</th><th>Seconds</th></tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr
                key={r.stamp}
                onClick={() => setStamp(r.stamp)}
                style={{ cursor: "pointer", fontWeight: r.stamp === active ? 700 : 400 }}
                aria-selected={r.stamp === active}
              >
                <td>{fmtStamp(r.stamp)}</td>
                <td>{r.run?.type || "—"}{r.run?.tables?.length ? ` (${r.run.tables.join(", ")})` : ""}</td>
                <td className="auto-table-mono">{r.run?.by || "—"}</td>
                <td>{r.dry_run ? "Dry run" : <b>LIVE</b>}</td>
                <td>{r.agents ?? "—"}</td>
                <td>{r.stopped ? <span className="auto-sched2-error">Stopped: {r.stopped}</span> : countsText(r.totals, r.dry_run)}</td>
                <td>{r.seconds ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {active && <RunDetail taskName={taskName} stamp={active} />}
    </div>
  );
}
